import Box from '@redbubble/design-system/src/Box';
import * as constants from '@redbubble/design-system/src/constants';
import Skeleton from '@redbubble/design-system/src/Skeleton';
import { defineMessages, useIntl } from '@redbubble/next/intl';
import { useNavigation } from '@redbubble/next-provider';
import { Key } from 'ts-key-enum';
import { ImageProtection } from '../../../../components/ImageProtection';

import {
  getImageRatioClass,
  imageRatio,
  Picture,
} from '../../../../components/Picture';

import { LoadingBoundary } from '../LoadingBoundary';
import styles from './ProductPreviewsCarousel.module.scss';
import { Preview } from './types';

export interface ProductPreviewCarouselProps {
  previews: Preview[];
  selectedPreview: Preview;
  artistName: string;
  productCategoryName: string;
  workTitle: string;
  onChange: (nextPreview: Preview) => void;
}

interface CarouselPictureProps {
  preview: Preview;
  index: number;
  productCategoryName: string;
  workTitle: string;
  artistName: string;
  totalPreviews: number;
}

const messages = defineMessages({
  thumbnailAltText: {
    defaultMessage:
      'Thumbnail {index} of {total}, {productCategoryName}, {workTitle} designed and sold by {artistName}.',
  },
});

export const CarouselPicture = ({
  preview,
  index,
  productCategoryName,
  workTitle,
  artistName,
  totalPreviews,
}: CarouselPictureProps) => {
  const intl = useIntl();

  return (
    <Picture
      ratioPaddingClass={getImageRatioClass(imageRatio.RATIO_PORTRAIT)}
      widthClass={styles.small}
      roundedCorners
    >
      <LoadingBoundary
        loadingComponent={
          <Skeleton ratio={constants.RATIO_PORTRAIT} animated roundedCorners />
        }
      >
        <Picture.Img
          src={preview.url}
          alt={intl.formatMessage(messages.thumbnailAltText, {
            index: index + 1,
            total: totalPreviews,
            productCategoryName,
            workTitle,
            artistName,
          })}
        />
      </LoadingBoundary>
    </Picture>
  );
};

export const ProductPreviewCarousel = ({
  previews,
  artistName,
  productCategoryName,
  workTitle,
  selectedPreview,
  onChange,
}: ProductPreviewCarouselProps) => {
  const { loading } = useNavigation();
  const getThumbnailState = (url: string): string => {
    if (loading) return styles.thumbnailInactive;
    if (url === selectedPreview.url) {
      return styles.thumbnailActive;
    }

    return styles.thumbnailInactive;
  };

  return (
    <Box className={styles.box} data-testid="product-preview-carousel">
      {previews.map((preview, index) => (
        <div
          key={`preview-${preview.previewTypeId}`}
          className={getThumbnailState(preview.url)}
          role="button"
          onClick={() => onChange(preview)}
          onKeyDown={(ev) => {
            if (ev.code === Key.Enter) {
              onChange(preview);
              ev.preventDefault();
            }
          }}
          tabIndex={0}
        >
          <ImageProtection>
            <CarouselPicture
              preview={preview}
              index={index}
              productCategoryName={productCategoryName}
              workTitle={workTitle}
              artistName={artistName}
              totalPreviews={previews.length}
            />
          </ImageProtection>
          <Box className={styles.loadingPlaceholder}>
            <Skeleton
              animated
              roundedCorners
              ratio={constants.RATIO_PORTRAIT}
            />
          </Box>
        </div>
      ))}
    </Box>
  );
};
